import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from "react-redux";
import NavBar from "../Navbar/Navbar.js";
import Header from '../Header/Header.js';
import './notes.css';
import { useUser } from '../UserContext';
import axiosInstance from '../axiosConfig.js';
import { useTranslation } from "react-i18next";
import { hideLoading, showLoading } from '../store/loadingSlice.js';

const NotesPage = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [isNavOpen, setIsNavOpen] = useState(true);
  const { username, companyName } = useUser();
  const licenseData = useSelector((state) => state.liscence.modules);
  const isLoading = useSelector((state) => state.loading.isLoading);

  const [notes, setNotes] = useState([]);
  const [selectedNote, setSelectedNote] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);

  const [newNote, setNewNote] = useState({
    date: '',
    title: '',
    body: ''
  });

  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  const toggleNav = () => setIsNavOpen(!isNavOpen);

  // Search by TITLE ONLY
  const filteredNotes = notes.filter(note =>
    (note.title || '').toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Fetch notes
  const fetchNOTES = async () => {
    dispatch(showLoading());

    const parameters = {
      url: `https://${licenseData.company_url}/s1services`,
      service: "SqlData",
      clientID: localStorage.getItem("token"),
      appId: licenseData.appid,
      SqlName: "9036",
      company: licenseData.company_id,
      user: licenseData.refid
    };

    try {
      const response = await axiosInstance.post("/api", parameters);
      const rows = response?.data?.rows ?? [];

      const mappedNotes = rows.map(row => ({
        id: String(row.ID),
        date: row.TRNDATE ? row.TRNDATE.slice(0, 10) : '',
        title: row.COMMENTS || '',
        body: row.REMARKS || ''
      }));

      setNotes(mappedNotes);
    } catch (err) {
      console.error("Error fetching notes:", err);
      setNotes([]);
      setError("Failed to load notes");
    } finally {
      dispatch(hideLoading());
    }
  };

  useEffect(() => {
    fetchNOTES();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // CREATE NOTE
  const handleCreateNote = async () => {
    const { date, title, body } = newNote;

    if (!date || !title.trim() || !body.trim()) {
      setError('Date, title and body are required');
      return;
    }

    setError('');
    dispatch(showLoading());

    try {
      const requestBody = {
        url: `https://${licenseData.company_url}/s1services`,
        service: "setData",
        clientID: localStorage.getItem("token"),
        appId: licenseData.appid,
        OBJECT: "SOTASK",
        KEY: "",
        data: {
          SOACTION: [{
            SERIES: 2100,
            TRNDATE: `${date} 00:00:00`,
            COMMENTS: title,
            REMARKS: body
          }]
        }
      };

      const response = await axiosInstance.post("/api", requestBody);

      if (response.data.success) {
        setShowCreateForm(false);
        setNewNote({ date: '', title: '', body: '' });
        await fetchNOTES();
      } else {
        setError(response.data.message || 'Failed to create note');
      }
    } catch (err) {
      console.error('Error creating note:', err);
      setError(err.response?.data?.message || 'Failed to create note');
    } finally {
      dispatch(hideLoading());
    }
  };

  // SAVE NOTE
  const handleSaveNote = async () => {
    const { date, title, body, id } = selectedNote;

    if (!date || !title.trim() || !body.trim()) {
      setError('Date, title and body are required');
      return;
    }

    setError('');
    dispatch(showLoading());

    try {
      const requestBody = {
        url: `https://${licenseData.company_url}/s1services`,
        service: "setData",
        clientID: localStorage.getItem("token"),
        appId: licenseData.appid,
        OBJECT: "SOTASK",
        KEY: id,
        data: {
          SOACTION: [{
            TRNDATE: `${date} 00:00:00`,
            COMMENTS: title,
            REMARKS: body
          }]
        }
      };

      const response = await axiosInstance.post("/api", requestBody);

      if (response.data.success) {
        setNotes(notes.map(n => n.id === id ? selectedNote : n));
        setSelectedNote(null);
      } else {
        setError(response.data.message || 'Failed to update note');
      }
    } catch (err) {
      console.error('Error updating note:', err);
      setError(err.response?.data?.message || 'Failed to update note');
    } finally {
      dispatch(hideLoading());
    }
  };

  return (
    <div id="module16">
      <div className={`container-card ${isNavOpen ? 'nav-open' : 'nav-closed'}`}>
        <NavBar toggleNav={toggleNav} />
        <div className="dashboard-content">
          <Header username={username} companyName={companyName} />
          <div className="main-content">
            <h2 className="section-title">{t("Notes")}</h2>

            <div className="projects-search-container">
              <div className="search-input-container">
                <input
                  type="text"
                  placeholder={t("search")}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="search-input"
                />
              </div>
            </div>

            <div className="message-list">
              {filteredNotes.map(note => (
                <div
                  key={note.id}
                  className="message-tab"
                  onClick={() => { setError(''); setSelectedNote({ ...note }); }}
                >
                  <div className="message-date">{note.date}</div>
                  <div className="message-title">{note.title}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Floating Create Button */}
      <button
        className="floating-create-btn"
        onClick={() => { setError(''); setShowCreateForm(true); }}
      >+</button>

      {/* CREATE NOTE MODAL */}
      {showCreateForm && (
        <div className="modal-overlay" onClick={() => setShowCreateForm(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <h3>{t("CreateNewNote")}:</h3>

            {error && <div className="error-message">{error}</div>}

            <div className="form-group">
              <label>{t("Date")}:</label>
              <input
                type="date"
                value={newNote.date}
                onChange={(e) => setNewNote({ ...newNote, date: e.target.value })}
              />
            </div>

            <div className="form-group">
              <label>{t("Subject")}:</label>
              <input
                value={newNote.title}
                onChange={(e) => setNewNote({ ...newNote, title: e.target.value })}
              />
            </div>

            <div className="form-group">
              <label>{t("Remarks")}:</label>
              <textarea
                className="note-body-textarea"
                rows={20}
                value={newNote.body}
                onChange={(e) => setNewNote({ ...newNote, body: e.target.value })}
              />
            </div>

            <div className="form-actions">
              <button onClick={() => setShowCreateForm(false)}>{t("cancel")}</button>
              <button onClick={handleCreateNote}>{t("create")}</button>
            </div>
          </div>
        </div>
      )}

      {/* EDIT NOTE MODAL */}
      {selectedNote && (
        <div className="modal-overlay" onClick={() => setSelectedNote(null)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <h3>{t("EditNote")}</h3>

            {error && <div className="error-message">{error}</div>}

            <div className="form-group">
              <label>{t("Date")}:</label>
              <input
                type="date"
                value={selectedNote.date}
                onChange={(e) =>
                  setSelectedNote({ ...selectedNote, date: e.target.value })
                }
              />
            </div>

            <div className="form-group">
              <label>{t("Subject")}:</label>
              <input
                value={selectedNote.title}
                onChange={(e) =>
                  setSelectedNote({ ...selectedNote, title: e.target.value })
                }
              />
            </div>

            <div className="form-group">
              <label>{t("Remarks")}:</label>
              <textarea
                className="note-body-textarea"
                rows={20}
                value={selectedNote.body}
                onChange={(e) =>
                  setSelectedNote({ ...selectedNote, body: e.target.value })
                }
              />
            </div>

            <div className="form-actions">
              <button onClick={() => setSelectedNote(null)}>{t("cancel")}</button>
              <button onClick={handleSaveNote}>{t("save")}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NotesPage;
